/**
 * Pick the assessment vertical from Department + Industry/Domain (+ title) — #237. The recruiter
 * should not have to know what a "vertical" is; the review step still shows the pick so the team
 * can see what was chosen until this mapping is trusted enough to hide it.
 *
 * Department leads (a Finance Analyst at a hospital is assessed as finance, not clinical); the
 * industry only decides when the department says nothing. Only keys the server actually offers
 * are returned, falling back to the industry-neutral "general". Pure — exported for tests.
 */

const DEPARTMENT_RULES: { key: string; re: RegExp }[] = [
  { key: "healthcare_clinical", re: /\b(clinical|critical care|nurs\w*|icu|medical|patient|physician|doctor|pharma\w*)\b/i },
  { key: "teaching", re: /\b(teach\w*|faculty|tutor\w*|academic|curriculum|instruct\w*)\b/i },
  { key: "creative_design", re: /\b(design|ux|ui|creative|brand|graphic|illustrat\w*|video)\b/i },
  { key: "marketing", re: /\b(marketing|growth|seo|content|social media|communications?|pr)\b/i },
  { key: "sales", re: /\b(sales|business development|revenue|account executive|b2b|pre-?sales)\b/i },
  { key: "human_resources", re: /\b(hr|human resources|people|talent|recruit\w*|payroll)\b/i },
  { key: "finance_operations", re: /\b(financ\w*|accounting|accounts|audit\w*|tax|treasury|controller|billing)\b/i },
  { key: "customer_support_bpo", re: /\b(support|customer (?:service|success|operations|experience)|call cent(?:er|re)|bpo|helpdesk)\b/i },
  { key: "project_delivery", re: /\b(project|program(?:me)?|delivery|pmo|scrum|implementation|functional consult\w*)\b/i },
  { key: "field_operations", re: /\b(field|supply chain|logistics|warehouse|manufactur\w*|plant|operations)\b/i },
  {
    key: "software_engineering",
    re: /\b(engineer\w*|developer|software|qa|quality assurance|tester|testing|sdet|technology|architect\w*|devops|sre|cloud|platform|data|ai|ml|backend|frontend|full ?stack|product experience|core services|it)\b/i,
  },
];

const INDUSTRY_RULES: { key: string; re: RegExp }[] = [
  { key: "healthcare_clinical", re: /\b(healthcare|hospital|clinic\w*|life sciences|medical)\b/i },
  { key: "teaching", re: /\b(education|edtech|school|universit\w*|learning)\b/i },
  { key: "retail_ops", re: /\b(retail|e-?commerce|store|qsr|hospitality)\b/i },
  { key: "customer_support_bpo", re: /\b(bpo|call cent(?:er|re)|outsourc\w*)\b/i },
  { key: "software_engineering", re: /\b(software|saas|technology|fintech|ai|data)\b/i },
];

export function suggestVerticalKey(
  input: { department?: string | null; industry?: string | null; title?: string | null },
  availableKeys: readonly string[],
): string {
  const has = (k: string): boolean => availableKeys.includes(k);
  const fallback = has("general") ? "general" : (availableKeys[0] ?? "general");
  const firstMatch = (text: string, rules: { key: string; re: RegExp }[]): string | null => {
    if (!text.trim()) return null;
    for (const r of rules) if (r.re.test(text) && has(r.key)) return r.key;
    return null;
  };
  const dept = `${input.department ?? ""} ${input.title ?? ""}`;
  const byDept = firstMatch(dept, DEPARTMENT_RULES);
  if (byDept === "customer_support_bpo" && has("retail_ops") && /\b(retail|e-?commerce|store)\b/i.test(input.industry ?? "")) {
    return "retail_ops";
  }
  return byDept ?? firstMatch(input.industry ?? "", INDUSTRY_RULES) ?? fallback;
}
